import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { trackUpgradeModalShown, trackUpgradeModalDismissed, trackUpgradeModalCTAClicked } from '../../services/analytics';

/**
 * Modal triggered by 403/429 upgrade-required API responses.
 * Listens for 'upgrade-required' CustomEvent dispatched by the axios interceptor.
 */
export default function UpgradeModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleUpgradeRequired = (event) => {
      setDetails(event.detail);
      setIsOpen(true);
      trackUpgradeModalShown({
        feature_key: event.detail?.feature_key,
        limit_used: event.detail?.used,
        limit_max: event.detail?.limit,
      });
    };

    window.addEventListener('upgrade-required', handleUpgradeRequired);
    return () => window.removeEventListener('upgrade-required', handleUpgradeRequired);
  }, []);

  if (!isOpen) return null;

  const isLimitReached = details?.limit !== undefined;
  const featureKey = details?.feature_key?.replace(/_/g, ' ');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 p-6 rounded-xl bg-card border border-border shadow-2xl">
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center space-y-4">
          <div className="p-3 rounded-full bg-primary/10">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>

          <h3 className="text-lg font-semibold text-foreground">
            {isLimitReached ? 'Usage Limit Reached' : 'Feature Not Available'}
          </h3>

          <p className="text-sm text-muted-foreground">
            {isLimitReached ? (
              <>
                You've used <span className="font-medium text-foreground">{details.used}</span> of{' '}
                <span className="font-medium text-foreground">{details.limit}</span>{' '}
                {featureKey} on your current plan.
                {details.resets && (
                  <> Resets on {new Date(details.resets).toLocaleDateString()}.</>
                )}
              </>
            ) : (
              <>
                <span className="capitalize font-medium text-foreground">{featureKey}</span>{' '}
                is not available on your current plan.
              </>
            )}
          </p>

          <p className="text-sm text-muted-foreground">
            Upgrade your plan to get more access.
          </p>

          <div className="flex gap-3 w-full">
            <button
              onClick={() => {
                trackUpgradeModalDismissed({ feature_key: details?.feature_key });
                setIsOpen(false);
              }}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-muted-foreground bg-white/5 border border-border rounded-lg hover:bg-white/10 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={() => {
                trackUpgradeModalCTAClicked({ feature_key: details?.feature_key });
                setIsOpen(false);
                navigate('/pricing');
              }}
              className="flex-1 px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
